// 05A — Rapport opérateurs.
//
// Deux choses qui parlaient des mêmes personnes vivaient séparées : le rapport
// opérateur, qu'on ne voyait qu'en marge du dépouillement, et les opérations à
// risque, qui avaient leur propre étape. Les tenir à part obligeait à faire le
// rapprochement de tête — « celui qui a 2,50 € d'écart, c'est bien celui qui a
// passé six annulations ? » — et c'est précisément ce rapprochement qui
// renseigne. Ici, tout ce que la journée dit d'une personne tient sur sa ligne.
//
// ÉTAPE D'INFORMATION. Rien ne s'y saisit ; la validation vaut accusé de
// lecture. La saisie reste au dépouillement (05B), et cet écran ne prétend
// jamais dire quoi faire — il dit ce qui est.
//
// L'ORDRE EST CELUI DE L'URGENCE, et il se trie sur le NOMBRE d'anomalies, pas
// sur un montant. Un écart de 2,50 € et six annulations à 400 € ne se comparent
// pas sur une même échelle ; prétendre le contraire donnerait un classement qui
// a l'air objectif et ne l'est pas.
//
// D14 : un écart n'est jamais vert et sa couleur ne dépend jamais de son signe.
// Le vert ne dit qu'une chose — « il n'y a pas d'écart ».

import { useMemo, useState } from 'react'
import { useRun } from '@/data/RunProvider'
import * as api from '@/data/api'
import { useReglages } from '@/data/reglages'
import {
  SEUILS_DEFAUT, bilan, constat, operateurs, parUrgence,
  type Nature, type Operateur,
} from '@/domain/operateurs'
import { etatOperations } from '@/domain/derive'
import { couleurEcart, fmtEcart } from '@/domain/ecart'
import { fmtEur, heure, pluriel } from '@/lib/format'
import { Card, Chip, Kpis, Lamp, Modale, Notice, Vide } from '@/ui/primitives'
import { useToast } from '@/ui/Toast'
import type { Couleur } from '@/ui/primitives'
import type { PropsEtape } from './types'

const TON: Record<Nature, Couleur> = { declaration: 'warn', ecart: 'warn', prelevement: 'warn' }

const MOT: Record<Nature, string> = {
  declaration: 'Déclaration',
  ecart: 'Écart',
  prelevement: 'Prélèvement',
}

/** Ce qu'on a écrit sur quelqu'un : le texte, par qui, et quand. */
interface Mot { texte: string; auteur?: string; at?: string }

const lireMot = (v: unknown): Mot | null => {
  const o = v && typeof v === 'object' ? (v as Record<string, unknown>) : {}
  const t = typeof o.text === 'string' ? o.text.trim() : ''
  if (!t) return null
  return {
    texte: t,
    auteur: typeof o.author === 'string' ? o.author : undefined,
    at: typeof o.at === 'string' ? o.at : undefined,
  }
}

export default function RapportOperateurs({ rapport, lectureSeule }: PropsEtape) {
  const reglages = useReglages()
  const { date, ecrireRapport, majRapport } = useRun()
  const toast = useToast()
  const [filtre, setFiltre] = useState<Nature | 'tous' | 'alerte' | 'risque'>('tous')
  const [ouvert, setOuvert] = useState<string | null>(null)
  const [commente, setCommente] = useState<Operateur | null>(null)

  const seuils = useMemo(() => ({
    declaration: Math.round(reglages.seuilDeclarationEspeces * 100),
    ecartTolere: SEUILS_DEFAUT.ecartTolere,
    ecartCritique: SEUILS_DEFAUT.ecartCritique,
    prelevement: SEUILS_DEFAUT.prelevement,
  }), [reglages.seuilDeclarationEspeces])

  const tous = useMemo(
    () => operateurs(rapport, seuils).sort(parUrgence),
    [rapport, seuils],
  )
  const b = useMemo(() => bilan(tous), [tous])
  const c = useMemo(() => constat(tous, seuils), [tous, seuils])
  const ops = useMemo(() => etatOperations(rapport), [rapport])

  // LE MÊME STOCKAGE QUE LE DÉPOUILLEMENT, délibérément.
  //
  // `operatorReports` est déjà écrit par l'étape 05B, indexé par badge, avec
  // l'auteur et l'heure posés par le serveur. En ouvrir un second ici aurait
  // fait deux endroits où chercher ce qu'on a écrit sur une personne — et c'est
  // toujours dans celui qu'on ne regarde pas que se trouve le mot qui comptait.
  const mots = useMemo(() => {
    const src = rapport?.operatorReports
    return src && typeof src === 'object' ? (src as Record<string, unknown>) : {}
  }, [rapport])

  const ecrireMot = (badge: string, texte: string) => {
    if (lectureSeule) return
    const propre = texte.trim()
    if (propre.length > 2000) { toast('MESSAGE TROP LONG — 2000 CARACTÈRES AU PLUS', true); return }
    const optimiste: Record<string, unknown> = { ...mots }
    if (propre) {
      optimiste[badge] = {
        ...(typeof mots[badge] === 'object' ? mots[badge] as object : {}),
        text: propre, at: new Date().toISOString(),
      }
    } else delete optimiste[badge]
    void ecrireRapport('mot sur un opérateur', { operatorReports: optimiste }, async () => {
      const carte = await api.enregistrerSignalement(date, badge, propre)
      majRapport({ operatorReports: carte })
    }, '05A').catch(() => {})
  }

  const visibles = tous.filter((o) => {
    if (filtre === 'tous') return true
    if (filtre === 'alerte') return o.alertes.length > 0
    if (filtre === 'risque') {
      return o.risque.annulations + o.risque.lignes + o.risque.remises + o.risque.prix > 0
    }
    return o.alertes.some((a) => a.nature === filtre)
  })

  // Sans le fichier des opérations, quatre zéros ressembleraient à une journée
  // propre. On le dit — c'est la seule chose honnête à afficher.
  const opsChargees = rapport?.operationsLoaded === true

  if (tous.length === 0) {
    return (
      <Vide>
        Aucun opérateur dans cette journée — l’import ORIKA n’a pas encore été fait.
      </Vide>
    )
  }

  return (
    <>
      {/* UNE PHRASE EN FRANÇAIS AVANT TOUT CHIFFRE. Six compteurs alignés
          disent ce qu'il y a ; ils ne disent pas s'il faut s'inquiéter. */}
      <div className={`op-phrase${b.avecAlerte > 0 ? ' alerte' : ''}`}>
        <Lamp c={b.avecAlerte > 0 ? 'warn' : 'ok'} />
        <span>{c.phrase}</span>
      </div>

      {/* LE CANARI. Si déclaré − théorique ne redonne pas l'écart, c'est le
          fichier qui est douteux, pas la caisse. Colorer un écart faux serait
          la pire façon de respecter D14 : afficher une anomalie inventée avec
          l'autorité d'une mesure. */}
      {c.colonnesSuspectes > 0 && (
        <Notice c="stop">
          <b>{pluriel(c.colonnesSuspectes, 'ligne')} dont l’écart ne se recalcule pas</b> —
          déclaré moins théorique ne redonne pas l’écart annoncé. Contrôle le fichier avant
          de conclure quoi que ce soit sur ces caisses : les écarts affichés ci-dessous
          pourraient n’exister que dans l’import.
        </Notice>
      )}

      <Kpis items={[
        { k: 'caissiers', v: b.personnes, s: 'des personnes' },
        { k: 'monnayeurs', v: b.monnayeurs, s: `${b.automates} caisses automatiques` },
        {
          k: 'à regarder', v: b.avecAlerte,
          s: b.avecAlerte === 0 ? 'rien ne cloche' : 'personnes concernées',
          couleur: b.avecAlerte === 0 ? 'var(--ok)' : 'var(--warn-b)',
        },
        { k: 'écarts', v: b.parNature.ecart, s: `au-delà de ${seuils.ecartTolere / 100} €` },
        {
          k: 'écart net', v: fmtEcart(c.net / 100),
          // Le net additionne les signes, le brut les valeurs absolues. Un net
          // nul sur un brut de 46 € n'est pas une journée plate : c'est une
          // journée où les erreurs se sont annulées entre elles.
          s: c.brut === Math.abs(c.net) ? 'aucune compensation'
             : `${fmtEur(c.brut / 100)} en valeur absolue`,
          couleur: c.brut === 0 ? 'var(--ok)' : 'var(--warn-b)',
        },
        {
          k: 'déclarations', v: b.parNature.declaration,
          s: `au-delà de ${fmtEur(seuils.declaration / 100)}`,
        },
        { k: 'prélèvements', v: b.parNature.prelevement, s: 'hors 500 €' },
        {
          k: 'opérations', v: opsChargees ? ops.lignes : '—',
          s: opsChargees ? 'annulations, remises, prix' : 'fichier non importé',
        },
      ]} />

      {!opsChargees && (
        <Notice c="warn">
          <b>Le fichier des opérations n’a pas été importé</b> — les annulations, remises,
          changements de prix et autorisations superviseur manquent. Ce qui est affiché ci-dessous
          ne porte donc que sur le rapport opérateur.
        </Notice>
      )}

      <div className="op-filtres">
        <button className={`btn gh sm${filtre === 'tous' ? ' on' : ''}`}
                aria-pressed={filtre === 'tous'} onClick={() => setFiltre('tous')}>
          Tout le monde ({tous.length})
        </button>
        <button className={`btn gh sm${filtre === 'alerte' ? ' on' : ''}`}
                aria-pressed={filtre === 'alerte'} onClick={() => setFiltre('alerte')}
                disabled={b.avecAlerte === 0}>
          À regarder ({b.avecAlerte})
        </button>
        {(Object.keys(MOT) as Nature[]).map((n) => (
          <button key={n} className={`btn gh sm${filtre === n ? ' on' : ''}`}
                  aria-pressed={filtre === n} onClick={() => setFiltre(n)}
                  disabled={b.parNature[n] === 0}>
            {MOT[n]} ({b.parNature[n]})
          </button>
        ))}
        {/* Les opérations à risque ne comptent pas dans « à regarder » — une
            annulation est le travail normal d'une caisse — mais on doit pouvoir
            y aller d'un clic. */}
        <button className={`btn gh sm${filtre === 'risque' ? ' on' : ''}`}
                aria-pressed={filtre === 'risque'} onClick={() => setFiltre('risque')}
                disabled={!opsChargees || ops.lignes === 0}>
          Opérations ({opsChargees ? ops.lignes : 0})
        </button>
      </div>

      {visibles.length === 0 ? <Vide>Personne ne répond à ce filtre.</Vide> : (() => {
        const chauds = visibles.filter((o) => o.alertes.length > 0)
        const calmes = visibles.filter((o) => o.alertes.length === 0)
        const ligne = (o: Operateur) => (
          <LigneOperateur key={o.badge} o={o} seuils={seuils}
                          mot={lireMot(mots[o.badge])}
                          commenter={lectureSeule ? undefined : () => setCommente(o)}
                          ouvert={ouvert === o.badge}
                          basculer={() => setOuvert(ouvert === o.badge ? null : o.badge)} />
        )
        return (
          <>
            {/* LA FRONTIÈRE COMPTÉE. Le tri seul dit qui est le pire ; il ne dit
                pas combien sont concernés. C'est ce chiffre-là qui fait passer
                la lecture de trois secondes à une. */}
            {chauds.length > 0 && (
              <div className="op-frontiere">À regarder · {chauds.length}</div>
            )}
            {chauds.map(ligne)}
            {calmes.length > 0 && (
              <div className="op-frontiere calme">
                Rien à signaler · {calmes.length}
                <span className="hint">
                  {' '}— « rien à signaler » n’est pas « écart nul » : un écart sous le seuil
                  y figure, en encre neutre
                </span>
              </div>
            )}
            {calmes.map(ligne)}
          </>
        )
      })()}

      {commente && (
        <ModaleMot
          o={commente} mot={lireMot(mots[commente.badge])}
          fermer={() => setCommente(null)}
          enregistrer={(t) => { ecrireMot(commente.badge, t); setCommente(null) }}
        />
      )}
    </>
  )
}

/**
 * Le mot qu'on laisse sur quelqu'un.
 *
 * Il n'appartient pas à cet écran : il vit dans `operatorReports`, que le
 * dépouillement lit et écrit aussi. Un mot posé ici se retrouve là-bas, et
 * l'inverse — c'est le même sujet, il n'a qu'un seul endroit.
 *
 * L'auteur et l'heure sont posés par le SERVEUR. Les laisser au client
 * permettrait d'écrire au nom d'un autre, ce qui viderait de son sens la seule
 * chose qu'un mot apporte : savoir qui l'a écrit.
 */
function ModaleMot({ o, mot, fermer, enregistrer }: {
  o: Operateur
  mot: Mot | null
  fermer: () => void
  enregistrer: (texte: string) => void
}) {
  const [texte, setTexte] = useState(mot?.texte ?? '')
  const propre = texte.trim()

  return (
    <Modale titre={`Mot sur ${o.nom}`}
            sous={o.genre === 'monnayeur' ? `machine ${o.badge}` : `badge ${o.badge}`}
            fermer={fermer}>
      {mot?.auteur && (
        <div className="hint" style={{ marginBottom: 8 }}>
          Écrit par {mot.auteur}{mot.at ? ` à ${heure(mot.at)}` : ''} — le réécrire le remplace.
        </div>
      )}
      <textarea rows={4} autoFocus value={texte} onChange={(e) => setTexte(e.target.value)}
                placeholder="Ce qu’on a constaté, ou ce qu’il faut savoir demain…" />
      <div className="hint" style={{ marginTop: 4 }}>
        Il suit la personne, pas la journée : le dépouillement le montre aussi.
      </div>
      <div className="btns">
        <button className="btn gh" onClick={fermer}>Annuler</button>
        {mot && (
          <button className="btn stop" onClick={() => enregistrer('')} title="Retirer ce mot">
            Effacer
          </button>
        )}
        <button className="btn ok" disabled={propre === (mot?.texte ?? '')}
                onClick={() => enregistrer(propre)}>
          Enregistrer
        </button>
      </div>
    </Modale>
  )
}

/**
 * Une personne, une ligne. Repliée elle porte l'essentiel ; dépliée elle montre
 * le chemin du calcul — fonds, apport, règlements espèces, théorique — parce
 * que « pourquoi 2,50 € ? » ne se répond pas avec le seul écart.
 */
function LigneOperateur({ o, seuils, mot, commenter, ouvert, basculer }: {
  o: Operateur
  seuils: { declaration: number; ecartTolere: number; ecartCritique: number; prelevement: number }
  mot: Mot | null
  /** Absent en lecture seule : une étape validée ne se réécrit pas. */
  commenter?: () => void
  ouvert: boolean
  basculer: () => void
}) {
  const r = o.rapport
  const alerte = o.alertes.length > 0

  return (
    <Card style={{ marginBottom: 8 }}>
      <div className="op-tete" onClick={basculer} role="button" tabIndex={0}
           onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') basculer() }}
           aria-expanded={ouvert}>
        <Lamp c={alerte ? 'warn' : o.genre === 'caissier' ? 'ok' : 'hw'} />
        <div className="op-qui">
          <b>{o.nom}</b>
          <span className="hint mo">
            {o.genre === 'monnayeur' ? `machine ${o.badge}` : `badge ${o.badge}`}
            {o.genre === 'automate' ? ' · caisse automatique' : ''}
          </span>
        </div>

        {/* Un automate n'a NI déclaration NI écart : afficher des zéros ferait
            croire à un écart nul là où il n'y a rien à comparer. */}
        {r ? (
          <>
            {/* Le « ! » suit la même règle que l'alerte : 650 € mesure le
                tiroir d'une personne, pas la cuve d'une machine. */}
            <Chiffre k="déclaré" v={fmtEur(r.declare / 100)}
                     alerte={o.genre === 'caissier' && r.declare > seuils.declaration} />
            <Chiffre k="écart" v={fmtEcart(r.ecart / 100)}
                     couleur={couleurEcart(r.ecart / 100)} />
            <Chiffre k="prélèvements"
                     v={r.prelevCount === 0 ? 'aucun' : `${r.prelevCount} × ${fmtEur(r.prelev / r.prelevCount / 100)}`}
                     alerte={r.prelevIrregulier} />
          </>
        ) : (
          <div className="op-sansrapport hint">
            pas de déclaration — une machine se vide, elle ne compte pas
          </div>
        )}

        <span className="sp" />
        <div className="op-alertes">
          {o.alertes.map((a, i) => (
            <Chip key={i} c={TON[a.nature]}>
              {MOT[a.nature].toUpperCase()}
              {a.cents != null ? ` ${fmtEur(a.cents / 100)}` : ''}
            </Chip>
          ))}
          {(() => {
            const n = o.risque.annulations + o.risque.lignes + o.risque.remises + o.risque.prix
            // Un FAIT, pas une alerte : une annulation est le travail normal
            // d'une caisse. Le ton reste neutre, et il ne compte pas dans
            // « à regarder ».
            return n > 0 ? <Chip>{n} OPÉRATION{n > 1 ? 'S' : ''}</Chip> : null
          })()}
          {mot && <Chip c="act">✎ MOT</Chip>}
          {!alerte && !mot && <Chip c="ok">RIEN À SIGNALER</Chip>}
        </div>
        <span className="op-chevron">{ouvert ? '⌃' : '⌄'}</span>
      </div>

      {ouvert && (
        <div className="op-detail">
          {r ? (
            <table className="t">
              <thead>
                <tr>
                  <th>Le chemin du calcul</th><th className="n">Montant</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>Fonds de caisse</td><td className="n">{fmtEur(r.fdc / 100)}</td></tr>
                <tr><td>Apport</td><td className="n">{fmtEur(r.apport / 100)}</td></tr>
                <tr><td>Règlements espèces</td><td className="n">{fmtEur(r.reglEsp / 100)}</td></tr>
                <tr><td>Prélèvements</td><td className="n">− {fmtEur(r.prelev / 100)}</td></tr>
                <tr>
                  <td><b>Théorique</b></td>
                  <td className="n"><b>{fmtEur(r.theorique / 100)}</b></td>
                </tr>
                <tr><td>Déclaré</td><td className="n">{fmtEur(r.declare / 100)}</td></tr>
                <tr>
                  <td><b>Écart</b></td>
                  <td className="n" style={{ color: couleurEcart(r.ecart / 100) }}>
                    <b>{fmtEcart(r.ecart / 100)}</b>
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <div className="hint" style={{ padding: '8px 12px' }}>
              Cette caisse automatique n’a pas de rapport : elle encaisse, elle ne déclare pas.
              Sa recharge se lit à l’étape 02A et son vidage à l’étape 06.
            </div>
          )}

          {/* Ce qu'une machine sait dire d'elle-même, et qu'aucun humain ne
              peut dire de lui : son activité, à côté de sa justesse. */}
          {o.machine && (
            <div className="op-risque">
              <span className="lbl">Activité de la machine</span>
              <table className="t" style={{ marginTop: 4 }}>
                <tbody>
                  <tr>
                    <td>Insertions</td>
                    <td className="n">{o.machine.insertions}</td>
                    <td className="n">{fmtEur(o.machine.especesInserees / 100)}</td>
                  </tr>
                  <tr>
                    <td>Rendus de monnaie</td>
                    <td className="n">{o.machine.rendus}</td>
                    <td className="n">{fmtEur(Math.abs(o.machine.montantRendu) / 100)}</td>
                  </tr>
                </tbody>
              </table>
              <div className="hint" style={{ marginTop: 4 }}>
                inventaire à {o.machine.inventaire || '—'}
                {o.machine.anomalie && ' · anomalie signalée par l’import'}
              </div>
            </div>
          )}

          <div className="op-risque">
            <span className="lbl">Mot sur cette personne</span>
            {mot ? (
              <div className="op-mot">
                <div>{mot.texte}</div>
                <div className="hint">
                  {mot.auteur ?? '—'}{mot.at ? ` · ${heure(mot.at)}` : ''}
                </div>
              </div>
            ) : (
              <div className="hint">Rien d’écrit.</div>
            )}
            {commenter && (
              <button className="btn gh sm" style={{ marginTop: 6 }} onClick={commenter}>
                ✎ {mot ? 'Modifier le mot' : 'Écrire un mot'}
              </button>
            )}
          </div>

          <div className="op-risque">
            <span className="lbl">Opérations à risque</span>
            {o.risque.annulations + o.risque.lignes + o.risque.remises + o.risque.prix === 0
              && o.autorisations.demandees + o.autorisations.donnees === 0 ? (
              <div className="hint">Aucune.</div>
            ) : (
              <div className="op-puces">
                {o.risque.annulations > 0 && <Chip c="warn">{pluriel(o.risque.annulations, 'ticket annulé', 'tickets annulés')}</Chip>}
                {o.risque.lignes > 0 && <Chip c="warn">{pluriel(o.risque.lignes, 'annulation de ligne', 'annulations de ligne')}</Chip>}
                {o.risque.remises > 0 && <Chip c="warn">{pluriel(o.risque.remises, 'remise')}</Chip>}
                {o.risque.prix > 0 && <Chip c="warn">{pluriel(o.risque.prix, 'changement de prix', 'changements de prix')}</Chip>}
                {/* Une autorisation a deux personnes : celle qui demande et
                    celle qui donne. N'en compter qu'une ne pose la question
                    qu'à moitié. */}
                {o.autorisations.demandees > 0 && <Chip>{o.autorisations.demandees} demandée{o.autorisations.demandees > 1 ? 's' : ''}</Chip>}
                {o.autorisations.donnees > 0 && <Chip c="act">{o.autorisations.donnees} autorisée{o.autorisations.donnees > 1 ? 's' : ''} par lui</Chip>}
                {o.risque.cents > 0 && (
                  <span className="hint mo">
                    {fmtEur(o.risque.cents / 100)} de marchandise touchée
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  )
}

function Chiffre({ k, v, alerte, couleur }: {
  k: string; v: string; alerte?: boolean; couleur?: string
}) {
  return (
    <div className="op-chiffre">
      <span className="lbl">{k}{alerte ? ' !' : ''}</span>
      <span className="mo" style={{ color: couleur ?? (alerte ? 'var(--warn-b)' : undefined) }}>
        {v}
      </span>
    </div>
  )
}
